import { memo, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { assetsChaptersPartialUpdate } from '@goldcast/api/content';
import { ChapterActionsMenuProps, ChapterType } from './ClipTimelineTypes';
import { getSegmentAtCurrentFrame, getSegmentTimeFromPlayerTime } from '../ClipNewTimeline/timeUtils';
import RowMenuItem from '@/Pages/Sessions/uiComponents/RowMenuItem';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { useAppContext } from '@/context/AppContext/AppContext';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import { MenuItemsLabelType, menuItems } from '@/Pages/Sessions/AssetsTable/AssetsTableUtils';
import { loadChapters } from '@/context/TranscriptContext/TranscriptContextUtils';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import useDialog from '@/components/organisms/useDialog';
import { isSplitsInTimelineEnabled } from '@/stores/clip';

function ChapterActionsMenu({
  type,
  chapterStartTime,
  chapterEndTime,
  onCreateClip,
  chapterId,
  onRenameChapter
}: ChapterActionsMenuProps) {
  const { eventId, broadcastId } = useParams<{ eventId: string; broadcastId: string }>();
  const [isClipCreated, setIsClipCreated] = useState(false);
  const [isDeletingChapter, setIsDeletingChapter] = useState(false);
  const { clipData } = useClipsContext();
  const { logger } = useAppContext();
  const navigate = useNavigate();

  const {
    isOpen: isRemoveChapterConfirmationOpen,
    openDialog: openRemoveChapterConfirmationDialog,
    closeDialog: closeRemoveChapterConfirmationDialog
  } = useDialog();

  useEffect(() => {
    if (isClipCreated) {
      navigate(`/${eventId}/${broadcastId}/clips`);
    }
  }, [isClipCreated]);

  const onCreateClipAction = useCallback(() => {
    setIsClipCreated(false);
    if (isSplitsInTimelineEnabled(clipData)) {
      const startSegment = getSegmentAtCurrentFrame(chapterStartTime, clipData);
      const endSegment = getSegmentAtCurrentFrame(chapterEndTime, clipData);
      if (startSegment?.id && endSegment?.id) {
        onCreateClip(
          getSegmentTimeFromPlayerTime(chapterStartTime, clipData, startSegment.id),
          getSegmentTimeFromPlayerTime(chapterEndTime, clipData, endSegment.id),
          startSegment.id,
          endSegment.id
        );
      }
    } else {
      onCreateClip(chapterStartTime, chapterEndTime);
    }
  }, [clipData, chapterStartTime, chapterEndTime, onCreateClip]);

  const onRemoveChapter = useCallback(() => {
    if (!chapterId || !broadcastId) {
      return;
    }
    setIsDeletingChapter(true);
    assetsChaptersPartialUpdate({
      id: chapterId,
      body: {
        is_active: false
      }
    })
      .then(async () => {
        const updatedChapters = await loadChapters(broadcastId);
        EventBus.dispatch(CustomEvents.UpdateChapters, {
          chapterData: updatedChapters?.chapters,
          chapterGenerationStatus: updatedChapters?.chapter_generation_status,
          chaptersLastUpdatedAt: updatedChapters?.latest_update_at
        });
        showSuccessToast('Chapter removed successfully');
      })
      .catch(err => {
        showErrorToast('Error while removing chapter');
        logger.error('Error while removing chapter', err);
      })
      .finally(() => {
        setIsDeletingChapter(false);
        closeRemoveChapterConfirmationDialog();
      });
  }, [chapterId, broadcastId]);

  const onAction = useCallback(
    (actionType: MenuItemsLabelType) => {
      switch (actionType) {
        case 'Rename Chapter':
          onRenameChapter();
          break;
        case 'Create Clip':
          onCreateClipAction();
          break;
        case 'Remove Chapter':
          openRemoveChapterConfirmationDialog();
          break;
      }
    },
    [onCreateClipAction, onRenameChapter, openRemoveChapterConfirmationDialog]
  );
  return (
    <>
      <RowMenuItem
        variant={type === ChapterType.TIMELINE ? 'compact' : 'default'}
        content={null}
        menuItems={menuItems['CHAPTER']}
        menuContainerStyle={
          type === ChapterType.TIMELINE ? '!fixed -translate-x-[calc(100%-20px)] -translate-y-[calc(100%+24px)]' : ''
        }
        positionVariant={type === ChapterType.TIMELINE ? '' : 'top-left'}
        onAction={onAction}
        forceShow={false}
      />
      <ConfirmationDialog
        isOpen={isRemoveChapterConfirmationOpen}
        onClose={closeRemoveChapterConfirmationDialog}
        onConfirm={onRemoveChapter}
        confirmLoading={isDeletingChapter}
        title="Remove Chapter"
        content="Are you sure you want to remove this chapter break in the video? You will not be able to revert this change."
        confirmLabel="Yes"
        cancelLabel="Cancel"
      />
    </>
  );
}

export default memo(ChapterActionsMenu);
